import React, { useState, useEffect, useRef } from 'react';

/* -- CAMERA STREAM URL HELPER -- */
const BRAND_SNAP={'Hikvision':ip=>'http://'+ip+'/ISAPI/Streaming/channels/101/picture','Dahua':ip=>'http://'+ip+'/cgi-bin/snapshot.cgi?channel=1','CP Plus':ip=>'http://'+ip+'/cgi-bin/snapshot.cgi?channel=1','Axis':ip=>'http://'+ip+'/axis-cgi/jpg/image.cgi?resolution=1280x720','Hanwha':ip=>'http://'+ip+'/stw-cgi/video.cgi?msubmenu=snapshot&action=view','Bosch':ip=>'http://'+ip+'/snap.jpg?JpegSize=L','Uniview':ip=>'http://'+ip+'/cgi-bin/snapshot.cgi'};
const BRAND_MJPG={'Hikvision':ip=>'http://'+ip+'/ISAPI/Streaming/channels/102/httpPreview','Dahua':ip=>'http://'+ip+'/cgi-bin/mjpg/video.cgi?channel=1&subtype=1','CP Plus':ip=>'http://'+ip+'/cgi-bin/mjpg/video.cgi?channel=1&subtype=1','Axis':ip=>'http://'+ip+'/axis-cgi/mjpg/video.cgi?resolution=640x480','Hanwha':ip=>'http://'+ip+'/stw-cgi/video.cgi?msubmenu=mjpeg&action=view','Bosch':ip=>'http://'+ip+'/video.mjpg','Uniview':ip=>'http://'+ip+'/cgi-bin/mjpg/video.cgi?channel=1'};
// Live view goes through the platform (same origin, https): /api/cameras/stream/:id?token=…
// The backend proxies to the AI Edge Server that serves this camera's site/tenant.
function getStreamUrl(cam){if(!cam||!cam.id)return{url:"",type:"none"};var t=localStorage.getItem("auravex_token")||"";return{url:"/api/cameras/stream/"+encodeURIComponent(cam.id)+"?token="+encodeURIComponent(t),type:"mjpeg"};}
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Legend, ComposedChart
} from 'recharts';
import { AURAVEX_LOGO } from './logo';
import { Badge, Dot, KPI, Panel, Prog, Pill, BtnStyles, Btn, Inp, Sel, Textarea, Modal, Toast, DataTable, PageHeader, AlertBox, Grid, Divider, CameraLiveModal } from './utils';
import { FIRModal } from './pages_ev';

// ============================================================
// LIVE API INTEGRATION - AURAVEX Backend
// ============================================================
const API_NODE   = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL : '';
const API_PYTHON = (typeof process !== 'undefined' && process.env?.REACT_APP_PYTHON_URL)
  ? process.env.REACT_APP_PYTHON_URL : 'http://localhost:8000';
const WS_URL_BASE = (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL)
  ? process.env.REACT_APP_WS_URL : 'ws://localhost:5000/ws';

const apiFetch = async (path, opts = {}, base = API_NODE) => {
  try {
    const token = localStorage.getItem('auravex_token');
    const res = await fetch(base + path, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      ...opts,
    });
    if (res.status === 401) {
      localStorage.removeItem('auravex_token');
      localStorage.removeItem('auravex_user');
      window.dispatchEvent(new CustomEvent('auravex:401'));
      return null;
    }
    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try { const j = await res.json(); errMsg = j.error || j.message || errMsg; } catch {}
      throw new Error(errMsg);
    }
    return await res.json();
  } catch (e) {
    console.warn(`[API] ${opts.method||'GET'} ${path}:`, e.message);
    throw e;  // re-throw so callers can handle it
  }
};
const pyFetch   = (p, o={}) => apiFetch(p, o, API_PYTHON);
const apiPost   = (p, d) => apiFetch(p, { method: 'POST',   body: JSON.stringify(d) });
const apiPut    = (p, d) => apiFetch(p, { method: 'PUT',    body: JSON.stringify(d) });
const apiPatch  = (p, d) => apiFetch(p, { method: 'PATCH',  body: JSON.stringify(d) });
const apiDelete = (p)    => apiFetch(p, { method: 'DELETE' });

// Map DB snake_case -> frontend camelCase
const mapTenant = r => r ? ({
  ...r,
  plan:    r.plan_id   ?? r.plan   ?? 'starter',
  maxCam:  r.max_cameras ?? r.max_cam ?? r.maxCam ?? 0,
  maxU:    r.max_users   ?? r.max_u   ?? r.maxU   ?? 0,
}) : null;
const mapCamera = r => r ? ({
  ...r,
  siteId:  r.site_id    ?? r.siteId  ?? '',
  res:     r.resolution ?? r.res     ?? '1080p',
  models:  Array.isArray(r.models) ? r.models : (typeof r.models==='string' ? JSON.parse(r.models||'[]') : []),
}) : null;
const mapEvent  = r => r ? ({
  ...r,
  caseId:  r.case_id   ?? r.caseId  ?? '',
  tId:     r.tenant_id ?? r.t_id    ?? r.tId    ?? '',
  siteId:  r.site_id   ?? r.siteId  ?? '',
  camId:   r.cam_id    ?? r.camId   ?? '',
  sev:     r.severity  ?? r.sev     ?? 'Medium',
  evidence: Array.isArray(r.evidence) ? r.evidence : [],
}) : null;
const mapUser   = r => r ? ({
  id:        r.id,
  name:      r.name,
  email:     r.email,
  role:      r.role,
  tenantId:  r.tenant_id   ?? r.tenantId ?? '',
  status:    r.status      ?? 'Active',
  mfa:       r.mfa_enabled ?? r.mfa      ?? false,
  last:      r.last_login  ?? r.last_seen ?? r.last ?? '',
  mobiles:   Array.isArray(r.mobiles)  ? r.mobiles  : (typeof r.mobiles ==='string' ? JSON.parse(r.mobiles  ||'[]') : []),
  emails:    Array.isArray(r.emails)   ? r.emails   : (typeof r.emails  ==='string' ? JSON.parse(r.emails   ||'[]') : [r.email||'']),
  notifyVia: (typeof r.notify_via==='object' && r.notify_via!==null) ? r.notify_via : (typeof r.notify_via==='string' ? JSON.parse(r.notify_via||'{}') : r.notifyVia || {email:true}),
}) : null;
const mapJetson = r => r ? ({ ...r, siteId: r.site_id??r.siteId, tId: r.tenant_id??r.t_id??r.tId }) : null;
const mapViol   = r => r ? ({ ...r, camId: r.cam_id??r.camId }) : null;
/* -- API payload converters (Frontend → Backend field names) -- */
const tenantToAPI  = t => ({ name:t.name, sector:t.sector, plan_id:t.plan||t.plan_id||'starter', contact:t.contact||'', billing:t.billing||'monthly', renewal:t.renewal||null, status:t.status, onboarded:t.onboarded||false });
const cameraToAPI  = c => ({ site_id:c.siteId||c.site_id, name:c.name, brand:c.brand||'Hikvision', type:c.type||'Fixed', ip:c.ip||'', resolution:c.res||c.resolution||'1080p', fps:c.fps||25, models:c.models||[], retention:c.retention||'30d', zone:c.zone||'', status:c.status, health:c.health });
const userToAPI    = u => ({ name:u.name, email:u.email||(u.emails&&u.emails[0])||'', role:u.role, tenant_id:u.tenantId||u.tenant_id||null, mobiles:u.mobiles||[], emails_list:u.emails||[], notify_via:u.notifyVia||u.notify_via||{email:true}, mfa_enabled:u.mfa||false, status:u.status });
const ruleToAPI    = r => ({ name:r.name, trigger:r.trigger||'', condition:r.condition||'', action:r.action||'', tenant_id:r.tId||r.tenant_id||'All', status:r.status||'Active' });

/* -- SECTOR AVATARS ------------------------------------------- */
const AVATAR_GOV  = "data:image/jpeg;base64,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";   // Male  - Government
const AVATAR_PVT  = "data:image/jpeg;base64,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"; // Female - Private / Industrial
const sectorAvatar = (sector) =>
  (sector==="Government"||sector==="Municipal"||sector==="Defence"||sector==="Police")
    ? AVATAR_GOV : AVATAR_PVT;

/* -- DESIGN TOKENS -- CLEAN WHITE LIGHT THEME ------------------- */
// ===== ZONE DRAWING SYSTEM =====
const ZoneOverlay = ({camId, streamUrl, width, height}) => {
  const [zones, setZones] = React.useState([]);
  const [drawing, setDrawing] = React.useState(false);
  const [mode, setMode] = React.useState('view'); // view, draw
  const [start, setStart] = React.useState(null);
  const [current, setCurrent] = React.useState(null);
  const canvasRef = React.useRef(null);
  const token = localStorage.getItem('auravex_token');

  // Load zones from backend
  React.useEffect(() => {
    if (!camId) return;
    fetch('/api/cameras/'+camId+'/zones', {
      headers: {'Authorization':'Bearer '+token}
    }).then(r=>r.json()).then(z=>{
      if (Array.isArray(z)) setZones(z);
    }).catch(()=>{});
  }, [camId]);

  // Save zones to backend
  const saveZones = (newZones) => {
    fetch('/api/cameras/'+camId+'/zones', {
      method:'PUT',
      headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},
      body: JSON.stringify({zones: newZones})
    }).then(()=>{ setZones(newZones); }).catch(()=>{});
  };

  const handleMouseDown = (e) => {
    if (mode !== 'draw') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setStart({x, y});
    setDrawing(true);
  };

  const handleMouseMove = (e) => {
    if (!drawing || mode !== 'draw') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setCurrent({x, y});
  };

  const handleMouseUp = () => {
    if (!drawing || !start || !current) { setDrawing(false); return; }
    const name = prompt('Zone name (e.g., Restricted Area, Robot Zone):');
    if (name) {
      const newZone = {
        id: 'Z-' + Date.now(),
        name: name,
        type: 'restricted',
        x1: Math.min(start.x, current.x),
        y1: Math.min(start.y, current.y),
        x2: Math.max(start.x, current.x),
        y2: Math.max(start.y, current.y),
        color: '#ff0000',
        active: true
      };
      const newZones = [...zones, newZone];
      saveZones(newZones);
    }
    setDrawing(false);
    setStart(null);
    setCurrent(null);
    setMode('view');
  };

  const deleteZone = (zoneId) => {
    const newZones = zones.filter(z => z.id !== zoneId);
    saveZones(newZones);
  };

  return React.createElement('div', {style:{position:'relative',display:'inline-block'}},
    // Zone overlay canvas
    React.createElement('div', {
      style:{position:'absolute',top:0,left:0,width:'100%',height:'100%',zIndex:10,
        cursor: mode==='draw' ? 'crosshair' : 'default',
        pointerEvents: mode==='draw' ? 'auto' : 'none'},
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp
    },
      // Draw existing zones
      zones.filter(z=>z.active).map(z =>
        React.createElement('div', {key:z.id, style:{
          position:'absolute',
          left:(z.x1*100)+'%', top:(z.y1*100)+'%',
          width:((z.x2-z.x1)*100)+'%', height:((z.y2-z.y1)*100)+'%',
          border:'2px solid '+(z.color||'#ff0000'),
          background:(z.color||'#ff0000')+'20',
          pointerEvents:'auto',
          zIndex:11
        }},
          React.createElement('span', {style:{
            position:'absolute',top:-18,left:0,
            background:z.color||'#ff0000',color:'#fff',
            fontSize:10,padding:'1px 6px',borderRadius:3,
            whiteSpace:'nowrap',fontWeight:600
          }}, z.name),
          mode==='view' ? null : React.createElement('span', {
            style:{position:'absolute',top:-18,right:0,
              background:'#333',color:'#fff',fontSize:10,
              padding:'1px 4px',borderRadius:3,cursor:'pointer'},
            onClick:(e)=>{e.stopPropagation();deleteZone(z.id)}
          }, '\u2715')
        )
      ),
      // Drawing rectangle
      drawing && start && current ? React.createElement('div', {style:{
        position:'absolute',
        left:(Math.min(start.x,current.x)*100)+'%',
        top:(Math.min(start.y,current.y)*100)+'%',
        width:(Math.abs(current.x-start.x)*100)+'%',
        height:(Math.abs(current.y-start.y)*100)+'%',
        border:'2px dashed #ff0000',
        background:'#ff000020'
      }}) : null
    ),
    // Controls
    React.createElement('div', {style:{
      position:'absolute',bottom:8,right:8,zIndex:20,
      display:'flex',gap:4,pointerEvents:'auto'
    }},
      React.createElement('button', {
        onClick:()=>setMode(mode==='draw'?'view':'draw'),
        style:{
          background:mode==='draw'?'#C0392B':'#1A5FB4',
          color:'#fff',border:'none',borderRadius:4,
          padding:'4px 10px',fontSize:11,cursor:'pointer',
          fontWeight:600
        }
      }, mode==='draw' ? '\u2715 Cancel' : '\u26A0 Draw Zone'),
      zones.length > 0 ? React.createElement('span', {
        style:{background:'#1A2235',color:'#C48A0E',
          padding:'4px 8px',borderRadius:4,fontSize:11,fontWeight:600}
      }, zones.length + ' zone' + (zones.length>1?'s':'')) : null
    )
  );
};
// ===== END ZONE DRAWING SYSTEM =====

const T = {
  /* -- Surface hierarchy -- */
  bg:"#F0F2F7",     /* Page background -- very light grey-blue */
  deep:"#FFFFFF",   /* Sidebar background */
  panel:"#FFFFFF",  /* Card panels */
  card:"#FFFFFF",   /* Inline cards */
  border:"#E2E8F3", /* Subtle borders */
  hi:"#EEF2FB",     /* Hover highlight */
  glow:"#DDE5F6",   /* Active glow */
  faint:"#F7F9FD",  /* Input backgrounds */
  /* Gold primary -- AURAVEX brand */
  accent:"#C48A0E", accentD:"#9A6B00", accentLo:"#C48A0E18",
  /* Electric blue */
  blue:"#1A5FB4",   blueLo:"#1A5FB414",
  /* Green */
  green:"#1E8A55",  greenLo:"#1E8A5514",
  /* Standard semantic */
  red:"#C0392B",    redLo:"#C0392B12",
  purple:"#6527BE", purpleLo:"#6527BE12",
  teal:"#0E7490",   tealLo:"#0E749012",
  orange:"#C05800", orangeLo:"#C0580012",
  pink:"#A21060",   pinkLo:"#A2106012",
  /* Text -- dark on white */
  text:"#1A2235",   muted:"#7A90B0",
  /* Silver */
  silver:"#4A607A",
  font:"'DM Sans',system-ui,sans-serif",
  mono:"'IBM Plex Mono','Fira Mono',monospace",
};

const statusColor = s => ({Online:"#10b981",Degraded:"#f59e0b",Offline:"#ef4444"})[s]||"#8F9BBA";

export const PageLiveStream = ({cameras,events,setEvents,toast,sendNotification,logAudit}) => {
  const [liveView, setLiveView] = useState(null);
  const [zones, setZones] = useState([]);
  React.useEffect(function(){if(!liveView)return;var t=localStorage.getItem("auravex_token");fetch("/api/cameras/"+liveView.id+"/zones",{headers:{Authorization:"Bearer "+t}}).then(function(r){return r.json()}).then(function(z){if(Array.isArray(z))setZones(z)}).catch(function(){});},[liveView]);
  const [selCam,   setSelCam]   = useState(cameras[0]?.id||null);
  const [layout,   setLayout]   = useState("2x2");   // 1x1 | 2x2 | 3x2 | 4x2
  const [activeGrid,setActiveGrid]=useState(cameras.slice(0,4).map(c=>c.id));
  const [tick,     setTick]     = useState(0);
  const [showIncident,setShowIncident]=useState(null);
  const [incForm,  setIncForm]  = useState({type:"Intrusion",sub:"Unauthorised Entry",sev:"High",notes:"",camId:"",notify:true});
  const fi = k => v => setIncForm(p=>({...p,[k]:v}));

  useEffect(()=>{ const t=setInterval(()=>setTick(p=>p+1),1400); return()=>clearInterval(t); },[]);

  const GRID_CONFIGS = {
    "1x1":{cols:1,count:1},
    "2x2":{cols:2,count:4},
    "3x2":{cols:3,count:6},
    "4x2":{cols:4,count:8},
  };
  const {cols,count} = GRID_CONFIGS[layout];

  // Update active grid when layout changes
  useEffect(()=>{
    setActiveGrid(cameras.slice(0,count).map(c=>c.id));
  },[layout,count]);

  const onlineCams = cameras.filter(c=>c.status==="Online");

  const logIncident = () => {
    const cam = cameras.find(c=>c.id===incForm.camId)||cameras.find(c=>c.id===selCam)||cameras[0];
    const ts  = new Date().toLocaleTimeString("en-IN");
    const id  = `EV-${String(events.length+1).padStart(3,"0")}`;
    const caseId = `CASE-${String(events.length+1).padStart(4,"0")}`;
    const newEv = {
      id, caseId, type:incForm.type, sub:incForm.sub,
      camId:cam?.id||"CAM-001", vehicle:"", conf:97,
      evidence:["frame","video"], sev:incForm.sev, status:"Open",
      esc:false, assigned:"unassigned", time:ts,
      notes:incForm.notes,
    };
    setEvents(p=>[newEv,...p]);
    logAudit&&logAudit("INCIDENT_LOGGED",`${id} -- ${incForm.type} @ ${cam?.id}`,incForm.sev);
    if(incForm.notify){
      sendNotification&&sendNotification(
        "ops@auravex.ai",
        `🚨 INCIDENT ${id}: ${incForm.type} -- ${incForm.sev}`,
        `Incident logged on camera ${cam?.id}.\nType: ${incForm.type}\nSub: ${incForm.sub}\nSeverity: ${incForm.sev}\nNotes: ${incForm.notes||"--"}\nTime: ${ts}\n\nReview: https://auravex.ai/events/${id}`,
        ["email","sms"]
      );
    }
    setShowIncident(null);
    setIncForm({type:"Intrusion",sub:"Unauthorised Entry",sev:"High",notes:"",camId:"",notify:true});
    toast(`${id} logged -- ${incForm.sev} incident recorded`);
  };

  // Simulated live feed tile
  const FeedTile = ({camId,large}) => {
    const cam = cameras.find(c=>c.id===camId);
    const [paused,setPaused] = useState(false);
    const [hovering,setHovering] = useState(false);
    if(!cam) return null;
    const c = ({Online:T.green,Degraded:T.accent,Offline:T.red})[cam.status]||T.muted;
    const isOnline = cam.status==="Online";
    const snapShot = () => {
      toast(`📸 Snapshot captured -- ${camId} @ ${new Date().toLocaleTimeString()}`);
    };
    return(
      <div
        onMouseEnter={()=>setHovering(true)}
        onMouseLeave={()=>setHovering(false)}
        style={{
          position:"relative",background:"#E8EFF8",borderRadius:8,
          border:`2px solid ${c}44`,overflow:"hidden",cursor:"pointer",
          aspectRatio:"16/9",transition:"border .15s",
          boxShadow:"none",
        }}>
        {/* Simulated camera feed background */}
        <div
          onClick={()=>{ setSelCam(camId); }}
          style={{
          position:"absolute",inset:0,
          background:isOnline&&!paused
            ? `linear-gradient(${135+tick*2}deg,#E4ECF8 0%,#D4E2F5 40%,#DCE8F6 60%,#E0ECF9 100%)`
            : paused ? "#CBD5E8" : "#D1DCF0",
          transition:"background 1.4s ease",
        }}/>
        {/* Scan lines overlay */}
        {isOnline&&<img src={getStreamUrl(cam).type==='snapshot'?(getStreamUrl(cam).url+(getStreamUrl(cam).url.includes('?')?'&':'?')+'t='+tick):getStreamUrl(cam).url} alt={cam.name||''} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",zIndex:1}} onError={function(e){e.target.style.opacity='0';if(e.target.parentNode){var d=e.target.parentNode.querySelector('.cam-offline');if(!d){d=document.createElement('div');d.className='cam-offline';d.style.cssText='position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);color:#C48A0E;font-size:12px;text-align:center';d.innerHTML='\u26A0 Camera Offline<br><span style=color:#7A90B0;font-size:10px>Edge agent not responding</span>';e.target.parentNode.style.position='relative';e.target.parentNode.appendChild(d);}}}} onLoad={function(e){e.target.style.opacity='1';var d=e.target.parentNode&&e.target.parentNode.querySelector('.cam-offline');if(d)d.remove();}}/>}
        {isOnline&&<div style={{position:"absolute",inset:0,backgroundImage:"repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,255,100,.03) 2px,rgba(0,255,100,.015) 4px)",pointerEvents:"none"}}/>}
        {/* Grid overlay */}
        {isOnline&&<div style={{position:"absolute",inset:0,backgroundImage:`linear-gradient(${T.green}08 1px,transparent 1px),linear-gradient(90deg,${T.green}08 1px,transparent 1px)`,backgroundSize:"33% 25%",pointerEvents:"none"}}/>}
        {/* AI detection boxes */}
        {isOnline&&liveView&&zones.length>0&&zones.map(function(z){return React.createElement("div",{key:z.id,style:{position:"absolute",left:(z.x1*100)+"%",top:(z.y1*100)+"%",width:((z.x2-z.x1)*100)+"%",height:((z.y2-z.y1)*100)+"%",border:"2px solid #ff0000",background:"rgba(255,0,0,0.12)",zIndex:15,pointerEvents:"none"}},React.createElement("span",{style:{position:"absolute",top:-16,left:0,background:"#ff0000",color:"#fff",fontSize:9,padding:"1px 6px",borderRadius:3,fontWeight:700}},z.name))})}
        {/* Status */}
        {!isOnline&&<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:4}}>
          <span style={{fontSize:22,opacity:.3}}>📵</span>
          <span style={{fontSize:9,color:c,fontFamily:T.mono}}>NO SIGNAL</span>
        </div>}
        {/* REC indicator */}
        {isOnline&&cam.recording&&<div style={{position:"absolute",top:6,left:8,display:"flex",alignItems:"center",gap:3}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:T.red,boxShadow:`0 0 ${tick%2?6:2}px ${T.red}`}}/>
          <span style={{fontSize:7,color:T.red,fontFamily:T.mono,fontWeight:800}}>REC</span>
        </div>}
        {/* Live badge */}
        {isOnline&&<div style={{position:"absolute",top:6,right:8}}>
          <span style={{fontSize:7,color:T.green,fontFamily:T.mono,fontWeight:700,background:"rgba(0,0,0,.7)",padding:"2px 4px",borderRadius:3,border:`1px solid ${T.green}44`}}>● LIVE</span>
        </div>}
        {/* Camera info bar */}
        <div style={{position:"absolute",bottom:0,left:0,right:0,background:"linear-gradient(transparent,rgba(10,20,50,.7))",padding:"8px 7px 5px",display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
          <div>
            <div style={{fontSize:large?11:9,fontWeight:700,color:T.text}}>{cam.id}</div>
            <div style={{fontSize:8,color:T.muted}}>{cam.name}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:7,fontFamily:T.mono,color:c}}>{cam.status}</div>
              <div style={{fontSize:7,fontFamily:T.mono,color:T.muted}}>{cam.fps>0?`${cam.fps}fps`:""} {cam.res}</div>
            </div>
            {/* Maximize button */}
            <button onClick={e=>{ e.stopPropagation(); setLiveView(cam); }}
              title="Open Full Live View"
              style={{width:20,height:20,borderRadius:4,background:`${T.blue}20`,
                border:`1px solid ${T.blue}44`,cursor:"pointer",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:11,color:T.blue,flexShrink:0,
                transition:"all .12s"}}
              onMouseEnter={e=>{ e.currentTarget.style.background=T.blue; e.currentTarget.style.color="#fff"; }}
              onMouseLeave={e=>{ e.currentTarget.style.background=`${T.blue}20`; e.currentTarget.style.color=T.blue; }}>
              ⛶
            </button>
          </div>
        </div>
        {/* Health bar */}
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:2,background:T.border}}>
          <div style={{height:"100%",width:`${cam.health}%`,background:cam.health>70?T.green:cam.health>40?T.accent:T.red,transition:"width .5s"}}/>
        </div>
        {/* Paused overlay */}
        {paused&&isOnline&&<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.55)"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <div style={{fontSize:28,opacity:.9}}>⏸</div>
            <div style={{fontSize:8,color:T.accent,fontFamily:T.mono,fontWeight:700,letterSpacing:2}}>PAUSED</div>
          </div>
        </div>}
        {/* Playback controls -- appear on hover */}
        {hovering&&isOnline&&<div
          style={{position:"absolute",bottom:22,left:"50%",transform:"translateX(-50%)",display:"flex",gap:5,alignItems:"center",zIndex:10}}
          onClick={e=>e.stopPropagation()}>
          <button onClick={()=>setPaused(p=>!p)} style={{background:"rgba(0,0,0,.75)",border:`1px solid ${T.accent}44`,borderRadius:5,color:T.text,cursor:"pointer",padding:"3px 8px",fontSize:11,display:"flex",alignItems:"center",gap:4}}>
            {paused?"> Play":"⏸ Pause"}
          </button>
          <button onClick={snapShot} style={{background:"rgba(0,0,0,.75)",border:`1px solid ${T.border}`,borderRadius:5,color:T.muted,cursor:"pointer",padding:"3px 7px",fontSize:11}}>
            📸
          </button>
          <button onClick={()=>setShowIncident(camId)} style={{background:"rgba(239,68,68,0.3)",border:`1px solid ${T.red}55`,borderRadius:5,color:T.red,cursor:"pointer",padding:"3px 7px",fontSize:10,fontWeight:700}}>
            🚨
          </button>
          <button onClick={()=>setLiveView(cam)}
            title="Maximize -- Full Live View"
            style={{background:"rgba(0,0,0,.75)",border:`1px solid ${T.blue}55`,borderRadius:5,color:T.blue,cursor:"pointer",padding:"3px 8px",fontSize:11,fontWeight:700}}>
            ⛶
          </button>
        </div>}
      </div>
    );
  };

  const selectedCam = cameras.find(c=>c.id===selCam);
  const camEvents   = events.filter(e=>e.camId===selCam).slice(0,8);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <PageHeader title="Live Camera Streaming & Incident Tracking" sub="Real-time feeds - AI detection overlays - One-click incident logging - Evidence auto-capture - Recall & replay">
        <div style={{display:"flex",gap:5}}>
          {["1x1","2x2","3x2","4x2"].map(l=><Pill key={l} label={l} active={layout===l} onClick={()=>setLayout(l)}/>)}
        </div>
        <Btn v="danger" onClick={()=>setShowIncident(selCam||cameras[0]?.id)}>🚨 Log Incident</Btn>
      </PageHeader>

      {/* KPI strip */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10}}>
        <KPI label="Live Feeds"   value={`${onlineCams.length}/${cameras.length}`} icon="📹" color={T.green}/>
        <KPI label="Recording"    value={cameras.filter(c=>c.recording).length}    icon="🔴" color={T.red}/>
        <KPI label="AI Active"    value={cameras.filter(c=>c.status==="Online"&&c.models.length>0).length} icon="🤖" color={T.accent}/>
        <KPI label="Open Incidents" value={events.filter(e=>e.status==="Open").length} icon="🚨" color={T.orange}/>
        <KPI label="Today Logged" value={events.length} icon="📋" color={T.blue}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 280px",gap:12}}>
        {/* Camera grid */}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{
            display:"grid",
            gridTemplateColumns:`repeat(${cols},1fr)`,
            gap:6,
          }}>
            {activeGrid.map(camId=><FeedTile key={camId} camId={camId} large={cols===1}/>)}
          </div>
          {/* Camera selector row */}
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 10px"}}>
            <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:1,marginBottom:6}}>SELECT CAMERAS FOR GRID</div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {cameras.map(cam=>{
                const active = activeGrid.includes(cam.id);
                const c = ({Online:T.green,Degraded:T.accent,Offline:T.red})[cam.status]||T.muted;
                return(
                  <button key={cam.id} onClick={()=>{
                    if(active){ if(activeGrid.length>1) setActiveGrid(p=>p.filter(x=>x!==cam.id)); }
                    else { setActiveGrid(p=>[...p.slice(0,count-1),cam.id]); }
                  }} style={{
                    padding:"3px 8px",borderRadius:5,fontSize:9,fontFamily:T.mono,cursor:"pointer",
                    background:active?`${c}22`:"transparent",
                    border:`1px solid ${active?c:T.border}`,
                    color:active?c:T.muted,fontWeight:active?800:400,transition:"all .12s",
                  }}>{cam.id}</button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right panel: selected camera detail + events */}
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {selectedCam&&(
            <Panel title={`${selectedCam.id} -- DETAIL`} accent={T.accent}>
              <div style={{display:"flex",flexDirection:"column",gap:6,fontSize:10}}>
                {[
                  ["Name",     selectedCam.name],
                  ["Brand",    selectedCam.brand],
                  ["Type",     selectedCam.type],
                  ["Resolution",selectedCam.res],
                  ["FPS",      selectedCam.fps+"fps"],
                  ["Status",   selectedCam.status],
                  ["Recording",selectedCam.recording?"Active":"Paused"],
                  ["Cloud recording", selectedCam.cloud_record?"On (48 h retention)":"Off"],
                  ["AI Models",selectedCam.models.slice(0,2).join(", ")||"None"],
                  ["IP",       selectedCam.ip],
                ].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"3px 0",borderBottom:`1px solid ${T.faint}`}}>
                    <span style={{color:T.muted}}>{l}</span>
                    <span style={{fontWeight:600,color:T.text,fontFamily:T.mono,fontSize:9}}>{v}</span>
                  </div>
                ))}
                <div style={{display:"flex",gap:5,marginTop:4,flexWrap:"wrap"}}>
                  <Btn sm v="danger" full onClick={()=>setShowIncident(selCam)}>🚨 Log Incident</Btn>
                  <Btn sm v="ghost" full onClick={()=>toast("📸 Camera snapshot captured")}>📸 Snapshot</Btn>
                </div>
              </div>
            </Panel>
          )}
          {/* Recent events on selected camera */}
          <Panel title={`INCIDENTS -- ${selCam||"ALL"}`} accent={T.red}>
            <div style={{display:"flex",flexDirection:"column",gap:5,maxHeight:300,overflowY:"auto"}}>
              {camEvents.length===0&&<div style={{fontSize:10,color:T.muted,textAlign:"center",padding:"14px 0"}}>No incidents on this camera</div>}
              {camEvents.map(ev=>(
                <div key={ev.id} style={{background:T.deep,borderRadius:6,padding:"7px 9px",border:`1px solid ${({Critical:T.red,High:T.accent,Medium:T.orange})[ev.sev]||T.border}22`}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:10,fontWeight:700,color:T.text}}>{ev.type}</span>
                    <Badge text={ev.sev} xs color={({Critical:T.red,High:T.accent,Medium:T.orange})[ev.sev]||T.muted}/>
                  </div>
                  <div style={{fontSize:9,color:T.muted}}>{ev.sub}</div>
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                    <span style={{fontSize:8,color:T.blue,fontFamily:T.mono}}>{ev.id}</span>
                    <span style={{fontSize:8,color:T.muted,fontFamily:T.mono}}>{ev.time}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                    <Dot s={ev.status}/>
                    <div style={{display:"flex",gap:4}}>
                      <Badge text={ev.status} xs color={({Open:T.red,Acknowledged:T.accent,Resolved:T.green})[ev.status]||T.muted}/>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      {/* Log Incident Modal */}
      <Modal open={!!showIncident} title="🚨 Log Incident / Save Evidence" onClose={()=>setShowIncident(null)}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <AlertBox type="warn">The incident is saved with its timestamp and any attached evidence; exports carry a SHA-256 checksum.</AlertBox>
          <Grid cols={2} gap={12}>
            <Sel label="Camera" value={incForm.camId||showIncident||cameras[0]?.id} onChange={fi("camId")} options={cameras.map(c=>({v:c.id,l:`${c.id} -- ${c.name}`}))}/>
            <Sel label="Incident Type" value={incForm.type} onChange={fi("type")} options={["Intrusion","Fire & Smoke","PPE Violation","Signal Violation","Vehicle Overspeed","Crowd Surge","Suspicious Behaviour","Equipment Tampering","Perimeter Breach","Unauthorised Access","Other"]}/>
            <Inp label="Sub-type / Detail" value={incForm.sub} onChange={fi("sub")} placeholder="e.g. Person without hardhat in zone"/>
            <Sel label="Severity" value={incForm.sev} onChange={fi("sev")} options={["Critical","High","Medium","Low","Info"]}/>
          </Grid>
          <Textarea label="Notes / Observations" value={incForm.notes} onChange={fi("notes")} placeholder="Describe what was observed, actions taken, relevant context..."/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,background:T.deep,borderRadius:8,padding:12}}>
            {[["📹 Video Clip","Auto-captured on log"],["🖼 Still Frame","from the detector"],["📍 GPS Location","Site coordinates"],["🕐 Timestamp","UTC + IST logged"]].map(([l,v])=>(
              <div key={l} style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:11}}>{l.split(" ")[0]}</span>
                <div><div style={{fontSize:10,fontWeight:700,color:T.text}}>{l.split(" ").slice(1).join(" ")}</div><div style={{fontSize:9,color:T.green}}>{v}</div></div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,background:T.deep,borderRadius:8,padding:"9px 12px"}}>
            <input type="checkbox" checked={incForm.notify} onChange={e=>setIncForm(p=>({...p,notify:e.target.checked}))} id="notif" style={{accentColor:T.accent}}/>
            <label htmlFor="notif" style={{fontSize:11,color:T.text,cursor:"pointer"}}>📧 Send SMS + Email alert to operations team</label>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setShowIncident(null)}>Cancel</Btn>
            <Btn v="danger" onClick={logIncident}>🚨 Save Incident & Evidence</Btn>
          </div>
        </div>
      </Modal>
      {liveView&&<CameraLiveModal cam={liveView} onClose={()=>setLiveView(null)} tick={0}/>}
    </div>
  );
};

/* ================================================================
   PAGE -- INCIDENT RECALL LOG (searchable, filterable history)
================================================================ */
export const PageIncidentLog = ({events,setEvents,toast,sendNotification,logAudit}) => {
  const [sf,setSf]     = useState("All");
  const [svf,setSvf]   = useState("All");
  const [tf,setTf]     = useState("All");
  const [search,setSearch] = useState("");
  const [sel,setSel]   = useState(null);
  const [exportId,setExportId] = useState(null);
  const [firEv,setFirEv] = useState(null);

  let rows = events;
  if(sf!=="All")   rows=rows.filter(e=>e.status===sf);
  if(svf!=="All")  rows=rows.filter(e=>e.sev===svf);
  if(tf!=="All")   rows=rows.filter(e=>e.type===tf);
  if(search.trim()) rows=rows.filter(e=>`${e.id} ${e.type} ${e.sub} ${e.camId} ${e.vehicle||""} ${e.caseId}`.toLowerCase().includes(search.toLowerCase()));

  const sel2 = events.find(e=>e.id===sel);
  const upd  = (id,ch) => { setEvents(p=>p.map(e=>e.id===id?{...e,...ch}:e)); apiPatch(`/api/events/${id}`,{status:ch.status,assigned:ch.assigned,escalated:ch.escalated,notes:ch.notes}).catch(()=>{}); toast("Incident updated"); };
  const types = ["All",...new Set(events.map(e=>e.type))];

  // Real SHA-256 of the exported bytes (Web Crypto). No fabricated hashes.
  const sha256Hex = async (bytes) => { const h = await crypto.subtle.digest("SHA-256", bytes); return Array.from(new Uint8Array(h)).map(b=>b.toString(16).padStart(2,"0")).join(""); };
  const saveBlob = (blob, name) => { const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=name; a.click(); URL.revokeObjectURL(url); };

  // Downloads the actual evidence attached to the event (snapshot images sent by the edge/cloud detector).
  const downloadEvidence = async (ev, eType) => {
    const items = Array.isArray(ev.evidence) ? ev.evidence.filter(x=>typeof x==="string" && x.startsWith("data:")) : [];
    if(!items.length){ toast("No evidence file is attached to this event", "error"); return; }
    for (let i=0;i<items.length;i++){
      const res = await fetch(items[i]); const blob = await res.blob();
      const hash = await sha256Hex(await blob.arrayBuffer());
      const ext = (blob.type.split("/")[1]||"jpg").replace("jpeg","jpg");
      saveBlob(blob, `${ev.caseId||ev.id}_evidence${items.length>1?"_"+(i+1):""}_${hash.slice(0,12)}.${ext}`);
    }
    toast(`${items.length} evidence file(s) downloaded with SHA-256 in the file name`);
  };

  // Exports the event record + its evidence as a JSON bundle whose SHA-256 is written alongside.
  const exportCase = async (ev) => {
    const me = (()=>{ try { return JSON.parse(localStorage.getItem("auravex_user")||"{}"); } catch { return {}; } })();
    const bundle = { incident: ev, exportedAt: new Date().toISOString(), exportedBy: me.email||me.name||"user", source: "AURAVEX event record" };
    const json = JSON.stringify(bundle, null, 2);
    const hash = await sha256Hex(new TextEncoder().encode(json));
    saveBlob(new Blob([json],{type:"application/json"}), `${ev.caseId||ev.id}_bundle_${hash.slice(0,12)}.json`);
    saveBlob(new Blob([`SHA-256 ${hash}  ${ev.caseId||ev.id}_bundle_${hash.slice(0,12)}.json\n`],{type:"text/plain"}), `${ev.caseId||ev.id}_bundle.sha256`);
    logAudit&&logAudit("EVIDENCE_EXPORTED",ev.id,"High");
    setExportId(ev.id);
    setTimeout(()=>setExportId(null),3000);
    toast(`${ev.id} exported with SHA-256 checksum`);
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <PageHeader title="Incident Recall Log" sub="Searchable archive of every event, with attached evidence and checksummed exports">
        <Btn v="ghost" sm onClick={()=>toast("📥 Exporting all data as CSV")}>📥 Export All CSV</Btn>
        <Btn sm>📋 Compliance Report</Btn>
      </PageHeader>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12}}>
        <KPI label="Total Incidents"  value={events.length}                                           icon="📋" color={T.blue}/>
        <KPI label="Open"             value={events.filter(e=>e.status==="Open").length}               icon="🔴" color={T.red}/>
        <KPI label="Resolved"         value={events.filter(e=>e.status==="Resolved").length}           icon="✅" color={T.green}/>
        <KPI label="Escalated"        value={events.filter(e=>e.esc).length}                           icon="^" color={T.purple}/>
        <KPI label="With Evidence"    value={events.filter(e=>e.evidence&&e.evidence.length>0).length} icon="🔒" color={T.teal}/>
      </div>

      {/* Search + Filters */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 14px",display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="🔍 Search by ID, type, camera, vehicle, case..."
          style={{flex:1,minWidth:200,background:T.deep,border:`1px solid ${T.hi}`,borderRadius:7,padding:"7px 12px",color:T.text,fontSize:12,fontFamily:T.font,outline:"none"}}
        />
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {["All","Open","Acknowledged","Resolved"].map(s=><Pill key={s} label={s} active={sf===s} onClick={()=>setSf(s)} color={({Open:T.red,Acknowledged:T.accent,Resolved:T.green})[s]}/>)}
        </div>
        <div style={{width:1,height:18,background:T.border}}/>
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {["All","Critical","High","Medium","Low"].map(s=><Pill key={s} label={s} active={svf===s} onClick={()=>setSvf(s)} color={({Critical:T.red,High:T.accent,Medium:T.orange})[s]}/>)}
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:sel2?"1fr 380px":"1fr",gap:14}}>
        {/* Incident table */}
        <Panel title={`${rows.length} INCIDENTS -- RECALL LOG`} action={
          <div style={{display:"flex",gap:5}}>
            {types.slice(0,5).map(t=><Pill key={t} label={t.split(" ")[0]} active={tf===t} onClick={()=>setTf(t)} color={T.teal}/>)}
          </div>
        }>
          <DataTable dense cols={[
            {k:"id",     l:"Incident ID", r:v=><span style={{fontFamily:T.mono,color:T.blue,fontSize:10,fontWeight:700}}>{v}</span>},
            {k:"caseId", l:"Case",        r:v=><span style={{fontFamily:T.mono,color:T.purple,fontSize:9}}>{v}</span>},
            {k:"time",   l:"Time",        r:v=><span style={{fontFamily:T.mono,color:T.muted,fontSize:9}}>{v}</span>},
            {k:"type",   l:"Type",        r:v=><span style={{fontWeight:700,fontSize:10}}>{v}</span>},
            {k:"sub",    l:"Detail",      r:v=><span style={{color:T.muted,fontSize:9,maxWidth:120,display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</span>},
            {k:"camId",  l:"Camera",      r:v=><Badge text={v} xs color={T.teal}/>},
            {k:"conf",   l:"AI%",         r:v=><span style={{fontFamily:T.mono,color:v>95?T.green:T.accent,fontWeight:700,fontSize:10}}>{v}%</span>},
            {k:"evidence",l:"Evid.",      r:v=><div style={{display:"flex",gap:2}}>{(v||[]).slice(0,2).map(x=><Badge key={x} text={x} xs color={T.teal}/>)}</div>},
            {k:"sev",    l:"Sev.",        r:v=><Badge text={v} xs color={({Critical:T.red,High:T.accent,Medium:T.orange,Low:T.blue})[v]||T.muted}/>},
            {k:"status", l:"Status",      r:v=><Dot s={v}/>},
            {k:"esc",    l:"Esc.",        r:v=>v?<Badge text="ESC" xs color={T.purple}/>:<span style={{color:T.muted,fontSize:9}}>--</span>},
            {k:"_act",l:"Actions",r:(v,row)=><div style={{display:"flex",gap:3}}>
              <Btn sm v="ghost" onClick={()=>setSel(sel===v?null:v)}>Recall</Btn>
              {row.status==="Open"&&<Btn sm v="warn" onClick={()=>upd(v,{status:"Acknowledged"})}>Ack</Btn>}
              {row.status!=="Resolved"&&<Btn sm v="success" onClick={()=>upd(v,{status:"Resolved"})}>Close</Btn>}
              <Btn sm v={exportId===v?"success":"teal"} onClick={()=>exportCase(row)}>{exportId===v?"✓ Done":"📦"}</Btn>
            </div>},
          ]} rows={rows}/>
        </Panel>

        {/* Detail panel */}
        {sel2&&(
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <Panel title={`RECALL -- ${sel2.id}`} accent={T.accent} action={<Btn sm v="ghost" onClick={()=>setSel(null)}>✕</Btn>}>
              {/* Case header */}
              <div style={{background:`linear-gradient(135deg,${T.faint},${T.hi})`,borderRadius:8,padding:12,marginBottom:10,border:`1px solid ${({Critical:T.red,High:T.accent,Medium:T.orange})[sel2.sev]||T.border}33`}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:800,color:T.text}}>{sel2.type}</div>
                    <div style={{fontSize:10,color:T.muted}}>{sel2.sub}</div>
                  </div>
                  <Badge text={sel2.sev} color={({Critical:T.red,High:T.accent,Medium:T.orange})[sel2.sev]||T.muted}/>
                </div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <Badge text={sel2.status} color={({Open:T.red,Acknowledged:T.accent,Resolved:T.green})[sel2.status]||T.muted} sm/>
                  {sel2.esc&&<Badge text="ESCALATED" color={T.purple} sm/>}
                  <Badge text={`${sel2.conf}% confidence`} color={T.teal} sm/>
                </div>
              </div>

              {/* Metadata */}
              <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
                {[["Incident ID",sel2.id],["Case ID",sel2.caseId],["Camera",sel2.camId],["Vehicle",sel2.vehicle||"N/A"],["Time",sel2.time],["Assigned",sel2.assigned],["Notes",sel2.notes||"--"]].map(([l,v])=>(
                  <div key={l} style={{display:"flex",gap:8,padding:"3px 0",borderBottom:`1px solid ${T.faint}`,fontSize:10}}>
                    <span style={{color:T.muted,minWidth:80,flexShrink:0}}>{l}</span>
                    <span style={{fontWeight:600,color:T.text,fontFamily:l==="Notes"?T.font:T.mono,fontSize:l==="Notes"?10:9,lineHeight:1.4}}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Evidence vault */}
              <div style={{marginBottom:12}}>
                <div style={{fontSize:9,color:T.muted,letterSpacing:1,marginBottom:7,fontWeight:700}}>🔒 EVIDENCE VAULT</div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  {(sel2.evidence||[]).map(e=>(
                    <div key={e} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:T.deep,borderRadius:7,padding:"7px 10px",border:`1px solid ${T.border}`}}>
                      <div style={{display:"flex",gap:7,alignItems:"center"}}>
                        <span style={{fontSize:16}}>{e==="frame"?"🖼":e==="video"?"🎬":"🔖"}</span>
                        <div><div style={{fontSize:10,fontWeight:700}}>{e==="frame"?"Still Frame":e==="video"?"Video Clip":"Plate Capture"}</div>
                          <div style={{fontSize:8,color:T.green}}>SHA-256 on export</div></div>
                      </div>
                      <Btn sm v="ghost" onClick={()=>downloadEvidence(sel2,e)}>v</Btn>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                <Sel label="Assign To" value={sel2.assigned||"unassigned"} onChange={v=>upd(sel2.id,{assigned:v})} options={["unassigned","Off. Sharma","S. Patil","K. Rao","Supervisor","Inspector","Legal Team"]}/>
                <Btn full sm v="warn"   onClick={()=>upd(sel2.id,{esc:true})}>^ Escalate</Btn>
                <Btn full sm v="purple" onClick={()=>setFirEv(sel2)}>📋 Generate FIR Draft</Btn>
                <Btn full sm v="teal"  onClick={()=>exportCase(sel2)}>📦 Export Evidence Bundle</Btn>
                <Btn full sm v="blue" onClick={()=>{
                  sendNotification&&sendNotification("supervisor@auravex.ai",`Supervisor Alert: ${sel2.id} -- ${sel2.type}`,`Incident ${sel2.id} (${sel2.caseId}) requires your attention.\n\nType: ${sel2.type}\nSeverity: ${sel2.sev}\nCamera: ${sel2.camId}\nStatus: ${sel2.status}\n\nPlease review in AURAVEX Incident Log.`,["email","sms"]);
                  upd(sel2.id,{notified:true});
                  toast("Supervisor notified via SMS + Email");
                }}>📧 Notify Supervisor</Btn>
                {sel2.status!=="Resolved"&&<Btn full sm v="success" onClick={()=>upd(sel2.id,{status:"Resolved"})}>✅ Close Incident</Btn>}
              </div>
            </Panel>
          </div>
        )}
      </div>
      <FIRModal ev={firEv} cameras={[]} onClose={()=>setFirEv(null)} toast={toast}/>
    </div>
  );
};

/* -- PAGE ACCESS DEFAULTS ------------------------------------------ */
export const ALL_PAGES = [
  {id:"dashboard",    icon:"[+]",  label:"Dashboard",           section:"OVERVIEW"},
  {id:"livestream",   icon:"📺", label:"Live Streaming",      section:"LIVE OPERATIONS"},
  {id:"tenants",      icon:"🏢", label:"Tenants",             section:"MANAGEMENT"},
  {id:"subscriptions",icon:"💳", label:"Subscriptions",       section:"MANAGEMENT"},
  {id:"users",        icon:"👥", label:"Users & RBAC",        section:"MANAGEMENT"},
  {id:"cameras",      icon:"📹", label:"Sites & Cameras",     section:"INFRASTRUCTURE"},
  {id:"infrastructure",icon:"⚡",label:"AI Edge Servers",     section:"INFRASTRUCTURE"},
  {id:"training",     icon:"✨", label:"Build a Model",       section:"AI ENGINE"},
  {id:"modelrequests",icon:"📨", label:"Request a Model",     section:"AI ENGINE"},
  {id:"models",       icon:"🧠", label:"AI Models",           section:"AI ENGINE"},
  {id:"licences",     icon:"🔑", label:"Model Licences",      section:"AI ENGINE"},
  {id:"events",       icon:"🚨", label:"Events & Evidence",   section:"OPERATIONS"},
  {id:"notifications",icon:"🔔", label:"Notifications",       section:"OPERATIONS"},
  {id:"reports",      icon:"📊", label:"Reports",             section:"ANALYTICS"},
  {id:"aiagent",      icon:"🤖", label:"AI Copilot",          section:"ANALYTICS"},
  {id:"notifyrecipients",icon:"📬",label:"Notify Recipients", section:"PLATFORM"},
  {id:"audit",        icon:"📋", label:"Audit Trail",         section:"SYSTEM"},
];

// Pages a tenant admin ("government" key) and a basic user ("private" key) can open by default.
// Everything else is Super Admin only. (The backend enforces data access regardless of this list.)
export const DEFAULT_PAGE_ACCESS = {
  government: ["dashboard","livestream","events","cameras","infrastructure","training","models","licences","notifications","reports","aiagent","users","audit"],
  private:    ["dashboard","livestream","cameras","events","modelrequests","notifications","reports","aiagent"],
};

/* -- NAV SECTIONS (Sidebar filters by role) --------------------------
   Pages that only showed static or simulated content (CO2/ESG, Sales CRM, Marketing, Agent Operations,
   AI Suite, AI Command, Data Annotate, Safety) are intentionally not listed until they are backed by real data. */
export const NAV_SECTIONS = [
  { label:"OVERVIEW", items:[ {id:"dashboard", icon:"[+]", label:"Dashboard", badge:null} ] },
  { label:"LIVE OPERATIONS", items:[
      {id:"livestream",     icon:"📺", label:"Live Streaming",     badge:"live"},
  ] },
  { label:"AI ENGINE", items:[
      {id:"training",       icon:"✨", label:"Build a Model",      badge:null},
      {id:"modelrequests",  icon:"📨", label:"Request a Model",    badge:null, roles:["private"]},
      {id:"models",         icon:"🧠", label:"AI Models",          badge:null},
      {id:"licences",       icon:"🔑", label:"Model Licences",     badge:null},
  ] },
  { label:"INFRASTRUCTURE", items:[
      {id:"cameras",        icon:"📹", label:"Sites & Cameras",    badge:"cameras"},
      {id:"infrastructure", icon:"⚡", label:"AI Edge Servers",    badge:null},
  ] },
  { label:"OPERATIONS", items:[
      {id:"events",         icon:"🚨", label:"Events & Evidence",  badge:"events"},
      {id:"notifications",  icon:"🔔", label:"Notifications",      badge:null},
      {id:"reports",        icon:"📊", label:"Reports",            badge:null},
      {id:"aiagent",        icon:"🤖", label:"AI Copilot",         badge:"ai"},
  ] },
  { label:"MANAGEMENT", items:[
      {id:"tenants",        icon:"🏢", label:"Tenants",            badge:"tenants"},
      {id:"subscriptions",  icon:"💳", label:"Subscriptions",      badge:null},
      {id:"users",          icon:"👥", label:"Users & RBAC",       badge:null},
  ] },
  { label:"PLATFORM", items:[
      {id:"notifyrecipients",icon:"📬",label:"Notify Recipients",  badge:null},
  ] },
  { label:"SYSTEM", items:[
      {id:"audit",          icon:"📋", label:"Audit Trail",        badge:null},
  ] },
];



// (hard-coded CAM-2035 zone overlay removed 2026-11-04; zones are drawn per camera in FeedTile)

